import React from 'react';
import { Button as UIButton } from './ui/button';
import { Loader2 } from 'lucide-react';

/**
 * Standardized button variants with BookingFlows brand colors
 * Brand Blue: #2563EB (used consistently across all primary actions)
 * Soft shadow: shadow-[0_8px_24px_rgba(0,0,0,0.04)] (matches card styling)
 */

// Primary Button (Main actions - BookingFlows Blue)
export const PrimaryButton = ({ children, loading, disabled, className = '', size, ...props }) => (
  <UIButton
    style={{ background: disabled ? undefined : '#2563EB' }}
    className={`hover:opacity-90 text-white ${size !== 'lg' ? 'h-9' : ''} ${className}`}
    disabled={disabled || loading}
    size={size}
    {...props}
  >
    {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
    {children}
  </UIButton>
);

/**
 * Layout Action Button — the standardized top-right "Add" action used across tab layouts
 * (Roles, Team, Assets, Services, …). Pill-shaped (matches the landing-page CTAs) and sits
 * on our custom soft shadow. Tabs that adopt this have been through the prod-polish pass.
 */
export const LayoutActionButton = ({ children, icon: Icon, loading, disabled, className = '', ...props }) => (
  <button
    type="button"
    style={{ background: disabled ? '#93b4f7' : '#2563EB' }}
    disabled={disabled || loading}
    className={`inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-md text-white text-sm font-medium shadow-[0_8px_24px_rgba(0,0,0,0.12)] transition-all hover:shadow-[0_10px_28px_rgba(37,99,235,0.30)] hover:opacity-95 active:scale-[0.98] disabled:cursor-not-allowed ${className}`}
    {...props}
  >
    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (Icon && <Icon className="w-4 h-4" />)}
    {children}
  </button>
);

// Secondary Button (Less important actions)
export const SecondaryButton = ({ children, loading, disabled, className = '', size, ...props }) => (
  <UIButton
    variant="outline"
    disabled={disabled || loading}
    className={`${size !== 'lg' ? 'h-9' : ''} ${className}`}
    size={size}
    {...props}
  >
    {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
    {children}
  </UIButton>
);

/**
 * Action Button Container - Wraps CTA buttons in a white container with soft shadow
 * Use this to wrap primary action buttons for consistent styling
 */
export const ActionButtonContainer = ({ children, className = '' }) => (
  <div className={`bg-white rounded-lg p-1.5 shadow-[0_8px_24px_rgba(0,0,0,0.04)] border border-gray-100 ${className}`}>
    {children}
  </div>
);

// Success Button (Positive actions - save, confirm, submit)
export const SuccessButton = ({ children, loading, disabled, className = '', size, ...props }) => (
  <UIButton
    className={`bg-green-600 hover:bg-green-700 text-white ${size !== 'lg' ? 'h-9' : ''} ${className}`}
    disabled={disabled || loading}
    size={size}
    {...props}
  >
    {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
    {children}
  </UIButton>
);

// Danger Button (Destructive actions - delete)
export const DangerButton = ({ children, loading, disabled, className = '', size, ...props }) => (
  <UIButton
    className={`bg-red-600 hover:bg-red-700 text-white ${size !== 'lg' ? 'h-9' : ''} ${className}`}
    disabled={disabled || loading}
    size={size}
    {...props}
  >
    {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
    {children}
  </UIButton>
);

// Warning Button (Caution actions)
export const WarningButton = ({ children, loading, disabled, className = '', ...props }) => (
  <UIButton
    className={`bg-orange-600 hover:bg-orange-700 text-white ${className}`}
    disabled={disabled || loading}
    {...props}
  >
    {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
    {children}
  </UIButton>
);

// Ghost Button (Minimal actions)
export const GhostButton = ({ children, loading, disabled, className = '', ...props }) => (
  <UIButton
    variant="ghost"
    disabled={disabled || loading}
    className={className}
    {...props}
  >
    {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
    {children}
  </UIButton>
);
